/**
 * Masking of `special` data (RFC-008 §9.5): message bodies, notification content and call
 * numbers are hidden until the viewer reveals them, and every reveal is audited first.
 */
import { Button } from "@uni-kit/ui";
import { Eye } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useDeviceServices } from "./services";

const DOT = "•";

/** Same shape, no content: letters and digits become dots, spacing is kept, length capped. Pure. */
export function maskText(s: string | null | undefined, max = 48): string {
  if (!s) return "";
  const cut = s.length > max ? s.slice(0, max) : s;
  return cut.replace(/\S/g, DOT) + (s.length > max ? DOT.repeat(3) : "");
}

/** A phone number with only its last two digits visible. Pure. */
export function maskNumber(n: string | null | undefined): string {
  if (!n) return "";
  const digits = n.replace(/\D/g, "");
  if (digits.length <= 2) return DOT.repeat(digits.length);
  return `${DOT.repeat(Math.min(8, digits.length - 2))}${digits.slice(-2)}`;
}

/** Which keys the viewer revealed on this screen. Local UI state; the audit trail is the server's. */
export function useRevealed() {
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());
  const has = useCallback((k: string) => keys.has(k) || keys.has("*"), [keys]);
  const add = useCallback((k: string) => setKeys((s) => new Set(s).add(k)), []);
  return { has, add } as const;
}

export interface RevealButtonProps {
  readonly coll: string;
  /** The record key, a group key ("thread:42") or "*" for everything in view. */
  readonly recordKey: string;
  readonly label?: string;
  readonly onRevealed: () => void;
}

/** "Reveal" → optional reason → audited → shown. If the audit write fails, the data stays masked. */
export function RevealButton({ coll, recordKey, label = "Reveal", onRevealed }: RevealButtonProps) {
  const services = useDeviceServices();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", ""); // DOM test environments without the dialog API
    }
    if (!open && el.open) {
      if (typeof el.close === "function") el.close();
      else el.removeAttribute("open");
    }
  }, [open]);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await services.reveal({ coll, key: recordKey, ...(reason.trim() ? { reason: reason.trim() } : {}) });
      setOpen(false);
      onRevealed();
    } catch (e) {
      setError(`Couldn't record the reveal, so it stays hidden. ${e instanceof Error ? e.message : ""}`.trim());
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {!open ? <Button size="sm" icon={Eye} onClick={() => setOpen(true)}>{label}</Button> : null}
      <dialog
        ref={dialog}
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        onCancel={(event) => { event.preventDefault(); if (!busy) setOpen(false); }}
        onClose={() => setOpen(false)}
        className="m-auto w-[min(30rem,calc(100vw-2rem))] rounded-panel border border-line-strong bg-surface p-0 text-ink-1 shadow-2xl backdrop:bg-black/55 backdrop:backdrop-blur-[2px]"
      >
        <form method="dialog" onSubmit={(event) => { event.preventDefault(); void confirm(); }}>
          <div className="border-b border-line px-5 py-4">
            <h2 id={`${id}-title`} className="text-lg font-semibold">Reveal protected content?</h2>
            <p id={`${id}-description`} className="mt-1 text-sm leading-6 text-ink-2">This action is recorded in the activity log. Only reveal content when it is needed for your work.</p>
          </div>
          <div className="px-5 py-4">
            <label className="grid gap-1.5 text-sm font-medium text-ink-1">
              Reason <span className="font-normal text-ink-3">(optional)</span>
              <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} placeholder="Why do you need to view this content?" className="w-full resize-none rounded-control border border-field bg-surface px-3 py-2 text-sm text-ink-1 placeholder:text-ink-3 focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none" />
            </label>
            {error ? <p role="alert" className="mt-3 text-sm text-tone-danger-ink">{error}</p> : null}
          </div>
          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <Button size="sm" variant="ghost" type="button" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" variant="primary" type="submit" disabled={busy}>{busy ? "Revealing…" : "Reveal"}</Button>
          </div>
        </form>
      </dialog>
    </>
  );
}
